import React, { createContext, useContext, useState, useCallback } from 'react';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

const Notification = ({ notification, onClose }) => {
  const typeConfig = {
    success: {
      bgColor: 'bg-green-50 border-green-200',
      iconColor: 'text-green-600',
      titleColor: 'text-green-800',
      textColor: 'text-green-700',
      icon: '✅'
    },
    error: {
      bgColor: 'bg-red-50 border-red-200',
      iconColor: 'text-red-600',
      titleColor: 'text-red-800',
      textColor: 'text-red-700',
      icon: '❌'
    },
    warning: {
      bgColor: 'bg-yellow-50 border-yellow-200',
      iconColor: 'text-yellow-600',
      titleColor: 'text-yellow-800',
      textColor: 'text-yellow-700',
      icon: '⚠️'
    },
    info: {
      bgColor: 'bg-blue-50 border-blue-200',
      iconColor: 'text-blue-600',
      titleColor: 'text-blue-800',
      textColor: 'text-blue-700',
      icon: 'ℹ️'
    }
  };

  const config = typeConfig[notification.type] || typeConfig.info;

  return (
    <div className={`max-w-sm w-full ${config.bgColor} border rounded-lg shadow-lg p-4 mb-4 animate-slide-in`}>
      <div className="flex">
        <div className="flex-shrink-0">
          <span className={`text-xl ${config.iconColor}`}>
            {notification.icon || config.icon}
          </span>
        </div>
        <div className="ml-3 flex-1">
          {notification.title && (
            <p className={`text-sm font-medium ${config.titleColor}`}>
              {notification.title}
            </p>
          )}
          <p className={`text-sm ${config.textColor} ${notification.title ? 'mt-1' : ''}`}>
            {notification.message}
          </p>
        </div>
        <div className="ml-4 flex-shrink-0 flex">
          <button
            onClick={() => onClose(notification.id)}
            className={`inline-flex ${config.iconColor} hover:opacity-75 transition-opacity`}
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = useCallback((notification) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      type: 'info',
      ...notification,
    };

    setNotifications(prev => [...prev, newNotification]);

    // Auto remove after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(notification => notification.id !== id));
    }, 5000);

    return id;
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const notify = {
    success: (message, title, options = {}) => addNotification({ type: 'success', message, title, ...options }),
    error: (message, title, options = {}) => addNotification({ type: 'error', message, title, ...options }),
    warning: (message, title, options = {}) => addNotification({ type: 'warning', message, title, ...options }),
    info: (message, title, options = {}) => addNotification({ type: 'info', message, title, ...options }),
  };

  return (
    <NotificationContext.Provider value={{ notify, addNotification, removeNotification }}>
      {children}
      
      {/* Notifications Container */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            notification={notification}
            onClose={removeNotification}
          />
        ))}
      </div>
    </NotificationContext.Provider>
  );
};